// The rpg layer's content (phase D1, gates D1.1 and D1.2), read from the tables and turned into the engine's shapes:
// the item table with its tabs, rarities, prices, slots, modifiers, use effects and forge levels (@rpg/items); the
// effects (@rpg/abilities); the progression (the curve, growth, points) and a tree per weapon; the class templates
// (loaded and validated, none played: decision 0050); enemy rewards (experience, gold and a fixed drop table: 0053);
// the shops, the recipes, the starting kit; and the market lab's stations, NPC stand-ins and things. Cross-table rules
// the schemas can't say are checked here and listed as issues; the engine's own checks run on the way (an unknown stat
// in a class template, a drop table naming a missing item: the gates' planted defects, `RPG_FAULTS`).
import { checkClassTemplates, checkProgression, type ClassTemplate, type EffectDef, type ProgressionDef, type SkillTree } from "@rpg/abilities";
import { ItemTable, checkDropTables, checkItems, checkRecipes, checkShops, type DropTable, type ItemDef, type RecipeDef, type SlotDef } from "@rpg/items";
import { resolveShops, type ShopInfo } from "@rpg-unit/shop";
import type { ContentRegistry } from "@rpg/content";
import type { SpikeContent } from "../content";
import type { ContentTables } from "../content/types.gen";
import { effectDefs } from "../combat/content";

/** Planted defects for the rpg content's checks (switched by `?faults=` or `__rpg.faults`; read when the content is). */
export const RPG_FAULTS = {
  /** A class template names an unknown stat (`luck`): the class-template validation must fail (gate D1.1). */
  classUnknownStat: false,
  /** An enemy's drop table names an item that doesn't exist: the drop-table check must fail (gate D1.2). */
  dropMissingItem: false,
  /** The shops pay their buy price (@rpg/items `SHOP_FAULTS.sellAtBuyPrice`): "selling below the buy price" must fail. */
  sellAtBuyPrice: false,
};
export type RpgFaultName = keyof typeof RPG_FAULTS;

/** The stat the planted class defect names, and the item the planted drop defect names. */
export const PLANTED = { stat: "luck", item: "dragon-scale", table: "enemy-brute" } as const;

/** A shop with what the screens show (@rpg-unit/shop's: its definition, keeper, name and standing discounts). */
export type { ShopInfo };

/** A station or NPC stand-in in the market lab. */
export type MarketPlace = ContentTables["market"];

/** Everything the rpg layer reads, references resolved. */
export interface RpgContent {
  spike: SpikeContent;
  items: ItemTable;
  itemRows: Map<string, ContentTables["items"]>;
  /** The combat weapon each weapon item wields, and back. */
  weaponOf: Map<string, string>;
  itemOfWeapon: Map<string, string>;
  effects: EffectDef[];
  effectRows: Map<string, ContentTables["effects"]>;
  attributes: string[];
  progression: ProgressionDef;
  healOnLevel: boolean;
  /** Each tree's id, its weapon, its name and its nodes' names. */
  trees: { id: string; weapon: string; name: string; nodes: Map<string, { name: string; info: string }> }[];
  classes: ClassTemplate[];
  rewards: Map<string, { xp: number; gold: number; drops: string }>;
  drops: Map<string, DropTable>;
  shops: Map<string, ShopInfo>;
  recipes: RecipeDef[];
  recipeNames: Map<string, string>;
  tabs: { id: string; label: string; stacks: number }[];
  slots: (SlotDef & { label: string })[];
  kit: { gold: number; items: { item: string; count: number }[]; equip: Record<string, string> };
  market: readonly MarketPlace[];
  marketThings: readonly ContentTables["market-things"][];
  glyph: { links: number; maxGlyphs: number; sink: number; speed: number };
  /** Problems across tables (empty when the content holds together). */
  issues: string[];
}

/** Read the rpg content. Throws on a missing tuning row; cross-table problems are listed in `issues` (the reader of the
 *  scene throws on them; the checks report them). */
export function readRpgContent(spike: SpikeContent, faults: Partial<typeof RPG_FAULTS> = RPG_FAULTS): RpgContent {
  const reg: ContentRegistry<ContentTables> = spike.registry;
  const issues: string[] = [];
  const tune = (id: string): number => {
    const v = reg.get("tuning", id)?.value;
    if (typeof v !== "number") throw new Error(`rpg: no tuning row "${id}"`);
    return v;
  };
  const attributes = reg.rows("stats").map((s) => s.id);
  const effects = effectDefs(reg);
  const effectRows = new Map(reg.rows("effects").map((e) => [e.id, e] as const));
  // items: the whole row, as @rpg/items' definitions
  const itemRows = new Map(reg.rows("items").map((r) => [r.id, r] as const));
  const defs: ItemDef[] = reg.rows("items").map((r) => ({
    id: r.id,
    name: r.name.id,
    stack: r.stack,
    tags: r.tags,
    tab: r.tab.id,
    ...(r.rarity ? { rarity: r.rarity } : {}),
    ...(r.price !== undefined ? { price: r.price } : {}),
    ...(r.slot ? { slot: r.slot } : {}),
    ...(r.modifiers ? { modifiers: r.modifiers } : {}),
    ...(r.use ? { use: r.use.map((u) => u.id) } : {}),
    ...(r.levels ? { levels: r.levels } : {}),
  }));
  const itemOpts = { rarities: ["common", "uncommon", "rare"], attributes, effects: effects.map((e) => e.id) };
  issues.push(...checkItems(defs, itemOpts));
  const items = new ItemTable(defs.filter((d, i) => defs.findIndex((x) => x.id === d.id) === i));
  const weaponOf = new Map<string, string>();
  const itemOfWeapon = new Map<string, string>();
  for (const r of reg.rows("items"))
    if (r.weapon) {
      weaponOf.set(r.id, r.weapon.id);
      itemOfWeapon.set(r.weapon.id, r.id);
      if (r.slot !== "weapon") issues.push(`item "${r.id}" wields a weapon but doesn't fit the weapon slot`);
    }

  // progression and the trees
  const prog = reg.rows("progression").find((p) => p.id === "hero");
  if (!prog) throw new Error("rpg: no progression row \"hero\"");
  const treeRows = reg.rows("skill-trees");
  const trees: SkillTree[] = treeRows.map((t) => ({
    id: t.id,
    nodes: t.nodes.map((n) => ({
      id: n.id,
      cost: n.cost,
      ...(n.requires ? { requires: n.requires } : {}),
      ...(n.minLevel !== undefined ? { minLevel: n.minLevel } : {}),
      ...(n.modifiers ? { modifiers: n.modifiers } : {}),
      ...(n.grants ? { grants: n.grants } : {}),
      ...(n.swaps ? { swaps: Object.fromEntries(n.swaps.map((s) => [s.from.id, s.to.id])) } : {}),
    })),
  }));
  const rewardRows = reg.rows("enemy-rewards");
  const progression: ProgressionDef = {
    curve: { maxLevel: prog.curve.maxLevel, ...(prog.curve.table ? { table: prog.curve.table } : {}), ...(prog.curve.base !== undefined ? { base: prog.curve.base } : {}), ...(prog.curve.exponent !== undefined ? { exponent: prog.curve.exponent } : {}) },
    growth: prog.growth as Record<string, number>,
    pointsPerLevel: prog.pointsPerLevel,
    trees,
    rewards: Object.fromEntries(rewardRows.map((r) => [r.enemy.id, r.xp])),
  };
  issues.push(...checkProgression(progression, attributes));
  for (const t of treeRows) if (!itemOfWeapon.has(t.weapon.id)) issues.push(`tree "${t.id}" is for weapon "${t.weapon.id}", which no item wields`);

  // class templates (decision 0050: loaded and validated; none played) — the D1.1 planted defect plants a stat here
  const classes: ClassTemplate[] = reg.rows("classes").map((c) => ({
    id: c.id,
    ...(c.start ? { start: { ...(c.start as Record<string, number>) } } : {}),
    ...(c.growth ? { growth: { ...(c.growth as Record<string, number>) } } : {}),
    ...(c.growthMode ? { growthMode: c.growthMode } : {}),
    ...(c.effects ? { effects: c.effects.map((e) => e.id) } : {}),
    ...(c.gear ? { gear: c.gear } : {}),
  }));
  if (faults.classUnknownStat && classes[0]) classes[0] = { ...classes[0], start: { ...(classes[0].start ?? {}), [PLANTED.stat]: 3 } };
  const itemTags = [...new Set(defs.flatMap((d) => d.tags))];
  issues.push(...checkClassTemplates(classes, { attributes, effects: effects.map((e) => e.id), itemTags }));

  // drop tables (every one: the breakables' and the enemies') — the D1.2 planted defect names a missing item
  const drops = new Map(
    reg.rows("drop-tables").map((r): [string, DropTable] => [
      r.id,
      { id: r.id, ...(r.pick ? { pick: r.pick } : {}), lines: r.lines.map((l) => ({ item: l.item.id, min: l.min, max: l.max, ...(l.chance !== undefined ? { chance: l.chance } : {}), ...(l.weight !== undefined ? { weight: l.weight } : {}) })) },
    ]),
  );
  if (faults.dropMissingItem) {
    const t = drops.get(PLANTED.table);
    if (!t) throw new Error(`rpg: no drop table "${PLANTED.table}" to plant the missing item in (the defect did not apply)`);
    drops.set(PLANTED.table, { ...t, lines: [...t.lines, { item: PLANTED.item, min: 1, max: 1 }] });
  }
  issues.push(...checkDropTables([...drops.values()], items));
  const rewards = new Map(rewardRows.map((r) => [r.enemy.id, { xp: r.xp, gold: r.gold, drops: r.drops.id }] as const));

  // shops and recipes
  // the shops: each row's terms from its preset (@rpg-unit/shop-presets), resolved by the shop feature
  const resolved = resolveShops(reg.rows("shops"), reg.rows("shop-presets"));
  issues.push(...resolved.issues);
  const shops = resolved.shops;
  issues.push(...checkShops([...shops.values()].map((s) => s.def), items));
  const recipes: RecipeDef[] = reg.rows("recipes").map((r) => ({
    id: r.id,
    station: r.station,
    inputs: r.inputs.map((i) => ({ item: i.item.id, count: i.count })),
    ...(r.cost !== undefined ? { cost: r.cost } : {}),
    output: "items" in r.output ? { items: r.output.items.map((o) => ({ item: o.item.id, count: o.count })) } : { upgrade: r.output.upgrade.id, level: r.output.level },
  }));
  issues.push(...checkRecipes(recipes, items));

  const tabs = reg.rows("inventory-tabs").map((t) => ({ id: t.id, label: t.label.id, stacks: t.stacks }));
  const slots = reg.rows("equip-slots").map((s) => ({ id: s.id, kind: s.kind, label: s.label.id }));
  const kitRow = reg.rows("kits").find((k) => k.id === "hero");
  if (!kitRow) throw new Error("rpg: no kit \"hero\"");
  const kit = { gold: kitRow.gold, items: kitRow.items.map((i) => ({ item: i.item.id, count: i.count })), equip: Object.fromEntries(Object.entries(kitRow.equip).map(([k, v]) => [k, v!.id])) };
  for (const k of Object.keys(kit.equip)) if (!slots.some((s) => s.id === k)) issues.push(`the kit equips unknown slot "${k}"`);

  return {
    spike,
    items,
    itemRows,
    weaponOf,
    itemOfWeapon,
    effects,
    effectRows,
    attributes,
    progression,
    healOnLevel: prog.healOnLevel ?? false,
    trees: treeRows.map((t) => ({ id: t.id, weapon: t.weapon.id, name: t.name.id, nodes: new Map(t.nodes.map((n) => [n.id, { name: n.name.id, info: n.info.id }] as const)) })),
    classes,
    rewards,
    drops,
    shops,
    recipes,
    recipeNames: new Map(reg.rows("recipes").map((r) => [r.id, r.name.id] as const)),
    tabs,
    slots,
    kit,
    market: reg.rows("market"),
    marketThings: reg.rows("market-things"),
    glyph: { links: tune("rpg.glyph.links"), maxGlyphs: tune("rpg.glyph.maxGlyphs"), sink: tune("rpg.glyph.sink"), speed: tune("rpg.glyph.speed") },
    issues,
  };
}
